// ============================================================
// EmailSignaturePanel.tsx
// Settings → Email & Microsoft: the user's EMAIL SIGNATURE editor (Stage E). Two ways to
// author it, chosen by a toggle:
//
//  • "Rich editor"  — a RichTextEditor (cleanBody, images + resize) for building
//                      a signature by hand. (Original behaviour.)
//  • "Paste HTML"   — paste raw signature HTML, or upload a .html file exported
//                      from a real mail client. ★ The pasted/imported HTML is
//                      SANITISED (DOMPurify — strip scripts/handlers/unsafe URLs)
//                      before preview AND before save. A live preview shows the
//                      sanitised result so the user sees exactly what will be
//                      stored + sent.
//
// Either way the (sanitised) HTML is saved to User.emailSignature via updateMeApi
// and mirrored into the auth store. The BACKEND re-sanitises on save AND on send
// (htmlSanitizer.ts) — that is the authoritative security gate; this client pass
// is convenience + preview. ComposeEmailModal auto-appends the signature to a
// NEW compose body. Empty signature is valid (clears it).
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { updateMeApi } from '../../api/casevoApi'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { sanitizeEmailHtml } from '../../utils/sanitizeEmailHtml'
import RichTextEditor from './RichTextEditor'
import {
  SettingsCard, SettingsSaved, SIcon,
  S_BTN_GHOST_SM, S_BTN_PRIMARY_SM, S_FORMFOOT, S_LABEL, S_NOTE,
} from '../settings/settingsUi'

type Mode = 'editor' | 'html'

const EmailSignaturePanel: React.FC = () => {
  const authUser = useCasevoStore((s) => s.authUser)
  const setAuthUser = useCasevoStore((s) => s.setAuthUser)
  const [mode, setMode] = useState<Mode>('editor')
  const [signature, setSignature] = useState<string>(authUser?.emailSignature ?? '')
  // Raw text in the Paste-HTML textarea (BEFORE sanitisation). Seeded from the
  // stored signature so switching to HTML mode shows the current markup.
  const [rawHtml, setRawHtml] = useState<string>(authUser?.emailSignature ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Re-seed if the stored signature changes (e.g. getMe hydrates after mount).
  useEffect(() => {
    setSignature(authUser?.emailSignature ?? '')
    setRawHtml(authUser?.emailSignature ?? '')
  }, [authUser?.emailSignature])

  // The HTML we'll actually save, depending on mode. In HTML mode the raw paste
  // is sanitised; in editor mode the editor's HTML is (the backend sanitises
  // both again regardless).
  const nextSignature = mode === 'html' ? sanitizeEmailHtml(rawHtml) : signature

  const handleSave = async (): Promise<void> => {
    if (saving) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await updateMeApi({ emailSignature: nextSignature })
      setAuthUser(updated)
      // Reflect the stored (sanitised) value back into both editors.
      setSignature(updated.emailSignature ?? '')
      setRawHtml(updated.emailSignature ?? '')
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save signature.')
    } finally {
      setSaving(false)
    }
  }

  // Read an uploaded .html/.htm file into the textarea (raw — sanitised on
  // preview + save). Kept client-side; nothing is uploaded anywhere.
  const onPickHtmlFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-picking the same file
    if (!file) return
    const reader = new FileReader()
    reader.onload = (): void => {
      setRawHtml(typeof reader.result === 'string' ? reader.result : '')
      // The Upload button sits in the footer for both modes (mockup); an
      // import always lands in Paste-HTML mode so the markup + sanitised
      // preview are visible before saving.
      setMode('html')
    }
    reader.readAsText(file)
  }

  const dirty = nextSignature !== (authUser?.emailSignature ?? '')
  // Unsaved signature edits: leaving Settings / this section, Back or a
  // refresh asks first (the same flag that enables Save).
  useUnsavedChangesGuard(dirty && !saving)
  // .sigtabs — bordered segmented control; active = mint + forest 600.
  const tabBase = 'h-full px-[12px] flex items-center text-[12.5px] transition-colors focus:outline-none casevo-focus'
  const tabActive = 'bg-casevo-mint text-casevo-forest dark:text-[#cfeedd] font-semibold'
  const tabIdle = 'text-casevo-brand dark:text-casevo-mid hover:bg-casevo-mint/40'

  return (
    <SettingsCard
      icon="pen"
      title="Email signature"
      sub="Added to the bottom of a new email when you compose. Edit or remove it before sending."
      flushBody
      right={
        // Mode toggle: build in the rich editor, or paste/import raw HTML.
        <div className="inline-flex h-[32px] border border-casevo-border rounded-[8px] overflow-hidden" role="tablist" aria-label="Signature editor mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'editor'}
            onClick={() => setMode('editor')}
            className={`${tabBase} ${mode === 'editor' ? tabActive : tabIdle}`}
          >
            Rich editor
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'html'}
            onClick={() => setMode('html')}
            className={`${tabBase} border-l border-casevo-border ${mode === 'html' ? tabActive : tabIdle}`}
          >
            Paste HTML
          </button>
        </div>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        // Extensions FIRST so Windows matches .html/.htm by suffix
        // regardless of the OS friendly type ("Chrome HTML Document"); the
        // MIME variants are a bonus. The read path (onPickHtmlFile) never
        // rejects by type — it just reads the file's text as HTML.
        accept=".html,.htm,.xhtml,text/html,application/xhtml+xml"
        onChange={onPickHtmlFile}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
      />
      {mode === 'editor' ? (
        <div className="mt-[12px]">
          <RichTextEditor value={signature} onChange={setSignature} enableImages cleanBody />
        </div>
      ) : (
        <div className="space-y-[12px] mt-[12px]">
          <p className={S_NOTE}>Paste your signature HTML below, or upload an .html file exported from your mail client.</p>
          <textarea
            value={rawHtml}
            onChange={(e) => setRawHtml(e.target.value)}
            spellCheck={false}
            aria-label="Signature HTML"
            placeholder="<table>…your signature HTML…</table>"
            className="w-full h-40 font-mono text-[12.5px] text-casevo-forest bg-casevo-input border border-casevo-border rounded-[10px] px-[12px] py-[10px] focus:outline-none focus:border-casevo-mid focus:shadow-[0_0_0_3px_rgba(64,145,108,0.15)] resize-y"
          />
          {/* ★ Live PREVIEW of the SANITISED result — what will actually be
              stored + sent. Rendered inside a SANDBOXED <iframe srcdoc> so the
              signature's own markup/CSS is fully ISOLATED from the settings page
              — a mail-client export can't restyle or "highlight" the app's other
              inputs. sandbox="" disables scripts/forms/etc.; the srcdoc is the
              already-sanitised HTML wrapped in a minimal light document. */}
          <div>
            <div className={S_LABEL}>Preview (sanitised, isolated)</div>
            <iframe
              title="Signature preview"
              sandbox=""
              className="w-full h-40 border border-casevo-border rounded-[10px] bg-white"
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:8px;font-family:'DM Sans',system-ui,sans-serif;font-size:14px;color:#1a3a2a;background:#fff}img{max-width:100%;height:auto}</style></head><body>${sanitizeEmailHtml(rawHtml)}</body></html>`}
            />
          </div>
        </div>
      )}

      <div className={S_FORMFOOT}>
        {saved && <SettingsSaved />}
        {error && <span className="text-[12.5px] text-casevo-danger" role="alert">{error}</span>}
        <span className="flex-1" />
        <button type="button" onClick={() => fileInputRef.current?.click()} className={S_BTN_GHOST_SM}>
          <SIcon name="upload" size={14} />
          Upload .html
        </button>
        <button
          type="button"
          onClick={() => { void handleSave() }}
          disabled={saving || !dirty}
          className={S_BTN_PRIMARY_SM}
        >
          {saving ? 'Saving…' : 'Save signature'}
        </button>
      </div>
    </SettingsCard>
  )
}

export default EmailSignaturePanel
